import React, { FC } from 'react';
import { Canvas, FabricImage, Line, IText } from 'fabric';
import { useCallback, useEffect, useRef } from 'react';

import { CROSSHAIR_ICON } from '../../../../../constants/images';
import { Calculate } from '../../../../../utilities';
import { KeypointerCanvasContainer, KeypointerCanvasSection } from './KeypointerCanvas.style';

interface IProps {
  activeKeypoints: any;
  activeFrame: any;
  dicom: any;
  scale: any;
  setKeypoints: any;
  showMeasurements: any;
}

const KeypointerCanvas: FC<IProps> = props => {
  const { activeKeypoints, activeFrame, dicom, scale, setKeypoints, showMeasurements } = props;
  const fabricRef = useRef<any>(null);
  const points = activeKeypoints;

  const handleKeypointerCanvasSectionOnClick = useCallback(
    event => {
      const isClickEvent = !event?.transform;

      if (!isClickEvent) return;

      const _activeKeypoints = window['keypoints'];
      const activeKeypointIndex = _activeKeypoints?.findIndex(keypoint => keypoint?.isActive);
      const noActiveKeypoints = activeKeypointIndex < 0 || !_activeKeypoints;

      if (noActiveKeypoints) return;

      const previousKeypoints = _activeKeypoints?.slice(0, activeKeypointIndex);
      const nextKeypoint = _activeKeypoints[activeKeypointIndex];

      const newKeypoints = [
        ...previousKeypoints,
        {
          ...nextKeypoint,
          isLabeled: true,
          isActive: false,
          x: event.pointer.x,
          y: event.pointer.y,
          uuid: nextKeypoint?.uuid || Math.random().toString(36),
        },
        {
          ..._activeKeypoints[activeKeypointIndex + 1],
          isActive: !_activeKeypoints[activeKeypointIndex + 1]?.isLabeled,
        },
        ..._activeKeypoints.slice(activeKeypointIndex + 2),
      ]?.slice(0, _activeKeypoints?.length);

      setKeypoints(newKeypoints);
    },
    [setKeypoints]
  );

  const onMovingObj = useCallback(
    event => {
      const id = event?.target?.id;

      const left = event?.target?.oCoords?.tl?.x;
      const top = event?.target?.oCoords?.tl?.y;

      const activeCrosshairText = fabricRef.current._objects.find(obj => obj.point?.uuid === id);
      const activeStartLine = fabricRef.current._objects.find(obj => obj.startPoint?.uuid === id);
      const activeEndLine = fabricRef.current._objects.find(obj => obj.endPoint?.uuid === id);
      const activeStartLineText = activeStartLine?.text;
      const activeEndLineText = activeEndLine?.text;

      // add offset on drag:
      activeCrosshairText?.set({ left: left + 16, top: top + 16 });
      activeStartLine?.set({ x1: left + 9, y1: top + 9 });
      activeEndLine?.set({ x2: left + 9, y2: top + 9 });

      // recalculate line length:
      const startLinePx = Calculate.lineLength(
        left + 9,
        top + 9,
        activeStartLine?.endPoint?.x,
        activeStartLine?.endPoint?.y,
        dicom?.physicalDeltaX
      ).toFixed(2);

      const endLinePx = Calculate.lineLength(
        activeEndLine?.startPoint?.x,
        activeEndLine?.startPoint?.y,
        left + 9,
        top + 9,
        dicom?.physicalDeltaX
      ).toFixed(2);

      // update waterline text:
      activeStartLineText?.set({
        left: (left + 9 + activeStartLine?.endPoint?.x) / 2 + 5,
        top: (top + 9 + activeStartLine?.endPoint?.y) / 2,
        text: `${startLinePx}cm`,
      });

      activeEndLineText?.set({
        left: (activeEndLine?.startPoint?.x + left + 9) / 2 + 5,
        top: (activeEndLine?.startPoint?.y + top + 9) / 2,
        text: `${endLinePx}cm`,
      });

      fabricRef.current.renderAll();
    },
    [dicom?.physicalDeltaX]
  );

  const onObjectRelease = useCallback(
    event => {
      const isDragEvent = !!event?.transform;

      if (!isDragEvent) return;

      const targetClass = event?.target?.keypointClass;

      const activeKeypoint = activeKeypoints?.find(
        keypoint => keypoint?.keypointClass === targetClass
      );
      const otherKeypoints = activeKeypoints?.filter(
        keypoint => keypoint?.keypointClass !== targetClass
      );
      const newKeypoints = [
        ...otherKeypoints,
        {
          ...activeKeypoint,
          x: event?.target?.left,
          y: event?.target?.top,
        },
      ];

      setKeypoints(newKeypoints);
    },
    [activeKeypoints, setKeypoints]
  );

  useEffect(() => {
    if (fabricRef.current && fabricRef.current._objects) {
      fabricRef.current._objects.forEach(obj => {
        fabricRef.current.remove(obj);
      });
      fabricRef.current.remove(...fabricRef.current.getObjects());
    }
    return () => {
      if (!JSON.stringify(window['keypoints'])) return;
      const _arr = JSON.parse(JSON.stringify(window['keypoints']));
      const objs = fabricRef.current.getObjects();
      _arr.map(e => {
        const _fabricobj = objs.find(c => c.id === e.id);
        if (_fabricobj) {
          e.x = _fabricobj.left;
          e.y = _fabricobj.top;
        }
        return e;
      });

      window['keypoints'] = _arr;
      // setKeypoints(_arr)
    };
  }, [activeFrame, fabricRef, showMeasurements]);

  useEffect(() => {
    fabricRef.current = new Canvas('KeypointerCanvas');
    fabricRef.current.setWidth(dicom?.pixelWidth);
    fabricRef.current.setHeight(dicom?.pixelHeight);

    fabricRef.current.on('mouse:down', handleKeypointerCanvasSectionOnClick, false);
    fabricRef.current.on('object:moving', onMovingObj, false);
    fabricRef.current.on('mouse:up', onObjectRelease, false);

    const disposeFabric = () => fabricRef.current.dispose();

    return () => disposeFabric();
  }, [
    activeFrame,
    activeKeypoints,
    dicom?.pixelHeight,
    dicom?.pixelWidth,
    handleKeypointerCanvasSectionOnClick,
    onMovingObj,
    onObjectRelease,
    showMeasurements,
  ]);

  useEffect(() => {
    window['keypoints'] = points;
  }, [points, activeFrame]);

  useEffect(() => {
    if (!points) return;

    fabricRef.current._objects.forEach(obj => fabricRef.current.remove(obj));
    fabricRef.current.remove(...fabricRef.current.getObjects());
    fabricRef.current.defaultCursor = 'pointer';

    const crosshairIconEl = document.getElementById('crosshair') as HTMLImageElement | null;
    if (!crosshairIconEl) return; // or handle gracefully

    const renderablePoints = points.filter(e => e.x && e.y && e.isLabeled);

    renderablePoints?.forEach(point => {
      const crosshair = new FabricImage(crosshairIconEl, {
        radius: 7,
        originX: 'center',
        originY: 'center',
        left: point?.x,
        top: point?.y,
        fill: 'red',
        id: point?.uuid,
        keypointClass: point?.keypointClass,
      });

      const crosshairText = new IText(`${point?.order}`, {
        left: point.x + 7,
        top: point.y + 7,
        fontSize: 12,
        selectable: false,
        fontWeight: 'bold',
        fill: 'yellow',
        point: point,
      });

      const keypointPair = renderablePoints.find(
        keypoint => point?.keypointPair === keypoint?.keypointClass
      );

      fabricRef.current.add(crosshair);
      fabricRef.current.add(crosshairText);

      crosshair.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        bl: false,
        br: false,
        tl: false,
        tr: false,
        mtr: false,
      });

      if (!keypointPair) return;

      const waterLine = new Line([keypointPair?.x, keypointPair?.y, point?.x, point?.y], {
        stroke: 'yellow',
        strokeWidth: 2,
        originX: 'center',
        originY: 'center',
        selectable: false,
        strokeDashArray: [10, 5],
        startPoint: keypointPair,
        endPoint: point,
      });

      const px = Calculate.lineLength(
        point.x,
        point.y,
        keypointPair.x,
        keypointPair.y,
        dicom?.physicalDeltaX
      ).toFixed(2);

      const text = new IText('' + parseFloat(px).toFixed(2) + 'cm', {
        left: (point.x + keypointPair.x) / 2 + 5,
        top: (point.y + keypointPair.y) / 2,
        fontSize: 12,
        selectable: false,
        fontWeight: 'bold',
        fill: 'yellow',
        // text: text,
      });

      if (showMeasurements) fabricRef.current.add(text);

      fabricRef.current.add(waterLine);
    });
  }, [points, activeFrame, showMeasurements, dicom?.physicalDeltaX]);

  return (
    <KeypointerCanvasContainer scale={scale} tabIndex={1000}>
      <KeypointerCanvasSection />
      <img src={CROSSHAIR_ICON} alt="crosshair" id="crosshair" width="15px" height="15px" hidden />
    </KeypointerCanvasContainer>
  );
};

export default KeypointerCanvas;
